import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, HTMLAttributes } from 'react';
import { motion, useAnimation, Variants, Transition } from 'framer-motion';

export interface PlusIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

export interface PlusIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered?: boolean;
}

const PlusIcon = forwardRef<PlusIconHandle, PlusIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    useEffect(() => {
      if (isHovered !== undefined) {
        if (isHovered) {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).startAnimation === 'function') {
            (ref.current as any).startAnimation();
          } else {
            handleMouseEnter({} as React.MouseEvent<HTMLDivElement>);
          }
        } else {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).stopAnimation === 'function') {
            (ref.current as any).stopAnimation();
          } else {
            handleMouseLeave({} as React.MouseEvent<HTMLDivElement>);
          }
        }
      }
    }, [isHovered, handleMouseEnter, handleMouseLeave, ref]);


    return (
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <motion.svg
          animate={controls}
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          transition={{ type: "spring", stiffness: 100, damping: 15 }}
          variants={{
            normal: {
              rotate: 0,
            },
            animate: {
              rotate: 180,
            },
          }}
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 12h14" />
          <path d="M12 5v14" />
        </motion.svg>
      </div>
    );
  }
);

PlusIcon.displayName = "PlusIcon";

export { PlusIcon };
